/**
 * API client.
 *
 * Three responsibilities beyond "call fetch":
 *
 * 1. **Silent refresh.** A 401 triggers one refresh attempt and one retry.
 *    Concurrent 401s share a single in-flight refresh -- without that, a screen
 *    that fires four requests on mount would rotate the refresh token four
 *    times, and rotation #2 would be rejected as replay by the server's reuse
 *    detection, logging the captain out for being efficient.
 *
 * 2. **Offline detection.** A network failure is not an error to surface as
 *    "something went wrong"; it is a normal condition in a fjord. Writes that
 *    can be queued are queued (see offline.ts); reads fall back to cache.
 *
 * 3. **Token custody.** The refresh token lives in localStorage. That is a
 *    deliberate, stated trade-off: an installed PWA on iOS cannot depend on a
 *    cookie surviving, and an XSS able to read localStorage can read the
 *    in-memory access token too, so the cookie buys confidentiality it cannot
 *    actually deliver here. The admin app, which runs in a normal browser tab,
 *    uses an HttpOnly cookie plus CSRF instead.
 */

const API_BASE: string = (import.meta.env.VITE_API_BASE as string) ?? 'http://localhost:8001'

const ACCESS_KEY = 'abg.access'
const REFRESH_KEY = 'abg.refresh'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Thrown when the request never reached the server. Callers may queue. */
export class OfflineError extends Error {
  constructor() {
    super('You are offline')
    this.name = 'OfflineError'
  }
}

// Access token in memory first so it survives no longer than the tab; mirrored
// to storage so a cold start does not force a refresh round trip before the
// first paint.
let accessToken: string | null = sessionStorage.getItem(ACCESS_KEY)

export function getAccessToken(): string | null {
  return accessToken
}

export function setTokens(access: string | null, refresh?: string | null): void {
  accessToken = access
  if (access) sessionStorage.setItem(ACCESS_KEY, access)
  else sessionStorage.removeItem(ACCESS_KEY)

  if (refresh !== undefined) {
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh)
    else localStorage.removeItem(REFRESH_KEY)
  }
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function clearSession(): void {
  accessToken = null
  sessionStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

type Listener = () => void
const sessionLostListeners = new Set<Listener>()

/** Fires when refresh fails and the captain has to sign in again. */
export function onSessionLost(fn: Listener): () => void {
  sessionLostListeners.add(fn)
  return () => sessionLostListeners.delete(fn)
}

// One shared refresh promise. See point 1 in the module docstring -- this is
// the whole reason it exists.
let refreshInFlight: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    const refresh = getRefreshToken()
    if (!refresh) return false
    try {
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      })
      if (!response.ok) {
        clearSession()
        return false
      }
      const data = (await response.json()) as { access_token: string; refresh_token?: string }
      setTokens(data.access_token, data.refresh_token ?? null)
      return true
    } catch {
      // Network failure, not an auth failure. Keep the tokens: they are
      // probably still good, we just cannot reach the server right now.
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Skip the refresh-and-retry dance (used by the refresh call itself). */
  noRetry?: boolean
  signal?: AbortSignal
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, noRetry = false, signal } = options

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`
    try {
      return await fetch(`${API_BASE}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      })
    } catch (cause) {
      if (signal?.aborted) throw cause
      throw new OfflineError()
    }
  }

  let response = await send()

  if (response.status === 401 && !noRetry && (await refreshSession())) {
    response = await send()
  }

  if (response.status === 401) {
    clearSession()
    sessionLostListeners.forEach((fn) => fn())
    throw new ApiError(401, 'Your session has expired. Please sign in again.')
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload: unknown = text ? safeJson(text) : undefined

  if (!response.ok) {
    throw new ApiError(response.status, detailOf(payload) ?? response.statusText, payload)
  }
  return payload as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * FastAPI puts the message in `detail`, but a 422 from pydantic puts a list of
 * per-field errors there instead. Both need to become one readable sentence --
 * "[object Object]" on a login screen is its own kind of outage.
 */
function detailOf(payload: unknown): string | null {
  if (typeof payload === 'string') return payload
  if (!payload || typeof payload !== 'object') return null
  const detail = (payload as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          const loc = Array.isArray((item as { loc?: unknown[] }).loc)
            ? (item as { loc: unknown[] }).loc.filter((p) => p !== 'body').join('.')
            : ''
          const msg = String((item as { msg: unknown }).msg)
          return loc ? `${loc}: ${msg}` : msg
        }
        return null
      })
      .filter(Boolean)
    if (messages.length) return messages.join('; ')
  }
  return null
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  base: API_BASE,
  refresh: refreshSession,
}
